<template>
  <div :class="darkMode ? 'dark' : ''"
    class="flex min-h-screen w-full bg-[#F5F5F7] text-[#1d1d1f] dark:bg-[#000000] dark:text-[#f5f5f7] transition-colors duration-500 font-sans">

    <div class="hidden md:flex flex-col shrink-0 h-screen sticky top-0 p-4 gap-4 z-50">
      <div
        class="h-16 flex-shrink-0 flex items-center justify-between gap-3 px-4 rounded-[24px] bg-white/80 dark:bg-[#1c1c1e]/80 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-sm transition-all duration-300"
        :class="[collapsed ? 'w-[72px] justify-center px-0' : 'w-72']">

        <router-link to="/user/dashboard" class="flex items-center justify-center gap-3 overflow-hidden">
          <div
            class="h-10 w-10 flex-shrink-0 grid place-items-center rounded-[14px] bg-gradient-to-br from-[#7B2CBF] to-[#5A189A] text-white shadow-lg shadow-purple-500/20">
            <span class="material-symbols-outlined text-[20px]">show_chart</span>
          </div>
          <div v-if="!collapsed" class="leading-tight whitespace-nowrap opacity-100 transition-opacity duration-300">
            <h1 class="text-[17px] font-semibold tracking-tight text-black dark:text-white">ControlEase</h1>
            <p class="text-[11px] text-[#86868b] font-medium">Financeiro Pro</p>
          </div>
        </router-link>

        <button v-if="!collapsed"
          class="rounded-full p-1.5 text-[#86868b] hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          @click="toggleSidebar">
          <span class="material-symbols-outlined text-[20px]">first_page</span>
        </button>
      </div>

      <aside :class="[
        'flex flex-col flex-1 bg-white/80 dark:bg-[#1c1c1e]/80 backdrop-blur-xl border border-white/20 dark:border-white/10 transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] rounded-[32px] overflow-hidden shadow-sm',
        collapsed ? 'w-[72px]' : 'w-72'
      ]">

        <div v-if="collapsed" class="flex justify-center py-4 border-b border-black/5 dark:border-white/5">
          <button
            class="h-10 w-10 flex items-center justify-center rounded-full text-[#86868b] hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            @click="toggleSidebar">
            <span class="material-symbols-outlined text-[22px]">last_page</span>
          </button>
        </div>

        <nav class="flex-1 overflow-y-auto overflow-x-hidden custom-scroll py-4 px-3 space-y-1">
          <template v-for="(item, index) in menuItems" :key="index">
            <div v-if="item.isHeader && !collapsed" class="px-3 mt-4 mb-2">
              <p class="text-[11px] font-bold uppercase tracking-wider text-[#86868b]">{{ item.label }}</p>
            </div>
            <div v-else-if="item.isHeader && collapsed" class="my-3 h-px bg-black/5 dark:bg-white/5 mx-2"></div>

            <div v-else-if="item.children" class="mb-1">
              <button @click="toggleSubmenu(item)" class="nav-item group transition-all duration-200" :class="[
                collapsed
                  ? 'w-11 h-11 justify-center mx-auto rounded-full px-0'
                  : 'w-full justify-between px-3 py-2.5 rounded-[14px]',
                isParentActive(item)
                  ? 'bg-black/5 dark:bg-white/5 text-black dark:text-white'
                  : 'text-[#1d1d1f] dark:text-[#f5f5f7]'
              ]">

                <div class="flex items-center" :class="collapsed ? 'justify-center w-full' : 'gap-3'">
                  <span class="material-symbols-outlined text-[22px] shrink-0" :class="isParentActive(item)
                    ? 'text-[#7B2CBF]'
                    : 'text-[#86868b] group-hover:text-black dark:group-hover:text-white'">
                    {{ item.icon }}
                  </span>
                  <span v-if="!collapsed" class="text-[15px] font-medium tracking-tight truncate">
                    {{ item.label }}
                  </span>
                </div>

                <span v-if="!collapsed"
                  class="material-symbols-outlined text-[18px] text-[#86868b] transition-transform duration-300"
                  :class="{ 'rotate-180': item.isOpen }">
                  expand_more
                </span>
              </button>

              <div v-if="!collapsed" class="grid transition-all duration-300 ease-in-out overflow-hidden"
                :class="item.isOpen ? 'grid-rows-[1fr] opacity-100 mt-1' : 'grid-rows-[0fr] opacity-0'">
                <div class="min-h-0">
                  <div class="pl-[14px] ml-[22px] border-l border-black/10 dark:border-white/10 space-y-1 pb-1">
                    <router-link v-for="child in item.children" :key="child.to" :to="child.to"
                      class="flex items-center gap-2 px-3 py-2 rounded-[10px] text-[14px] transition-colors relative"
                      :class="isActive(child.to)
                        ? 'text-[#7B2CBF] font-semibold bg-[#7B2CBF]/10'
                        : 'text-[#6e6e73] dark:text-[#a1a1a6] hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'">
                      <span>{{ child.label }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <router-link v-else :to="item.to" class="nav-item group mb-1 transition-all duration-200" :class="[
              isActive(item.to) ? 'active' : '',
              collapsed
                ? 'w-11 h-11 justify-center mx-auto rounded-full px-0'
                : 'w-full px-3 py-2.5 rounded-[14px] justify-start'
            ]" :title="collapsed ? item.label : null">

              <div class="flex items-center" :class="collapsed ? 'justify-center w-full' : 'gap-3'">
                <span class="material-symbols-outlined text-[22px] shrink-0 transition-colors duration-200" :class="isActive(item.to)
                  ? 'text-white'
                  : 'text-[#86868b] group-hover:text-black dark:group-hover:text-white'">
                  {{ item.icon }}
                </span>
                <span v-if="!collapsed"
                  class="text-[15px] font-medium tracking-tight whitespace-nowrap transition-colors duration-200"
                  :class="isActive(item.to) ? 'text-white' : 'text-[#1d1d1f] dark:text-[#f5f5f7]'">
                  {{ item.label }}
                </span>
              </div>
            </router-link>
          </template>
        </nav>

        <div class="p-3 mt-auto border-t border-black/5 dark:border-white/5 bg-white/50 dark:bg-black/20">
          <div class="flex flex-col gap-2" :class="collapsed ? 'items-center' : ''">
            <button @click="logout"
              class="nav-item group transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-900/20" :class="collapsed
                ? 'w-11 h-11 justify-center mx-auto rounded-full px-0'
                : 'w-full px-3 py-2.5 rounded-[14px] justify-start gap-3'">
              <span
                class="material-symbols-outlined text-[22px] text-red-500 transition-transform group-hover:-translate-x-0.5 shrink-0">
                logout
              </span>
              <span v-if="!collapsed" class="text-[15px] font-medium text-red-600 dark:text-red-400">
                Sair
              </span>
            </button>
          </div>
        </div>
      </aside>
    </div>

    <div class="flex-1 min-w-0 flex flex-col relative z-0">
      <header class="sticky top-0 z-40 px-4 pb-2 pt-[calc(env(safe-area-inset-top)+16px)]">
        <div
          class="absolute top-0 left-0 right-0 h-[calc(128px+env(safe-area-inset-top))] bg-gradient-to-b from-[#F5F5F7] to-transparent dark:from-[#000000] pointer-events-none">
        </div>
        <div class="relative mx-auto w-full max-w-[1400px]">
          <div
            class="h-[72px] rounded-[24px] bg-white/70 dark:bg-[#1c1c1e]/70 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] dark:shadow-none border border-white/40 dark:border-white/5 flex items-center justify-between px-4 sm:px-6 transition-all duration-300">

            <div class="flex items-center gap-4">
              <button
                class="md:hidden rounded-full p-2 text-[#1d1d1f] dark:text-white hover:bg-black/5 dark:hover:bg-white/10"
                @click="mobileOpen = true">
                <span class="material-symbols-outlined">menu</span>
              </button>
              <div class="flex items-center gap-3">
                <h2 class="text-[17px] font-semibold tracking-tight text-[#1d1d1f] dark:text-white leading-tight">
                  Olá, {{ userName.split(' ')[0] }}
                </h2>
              </div>
            </div>

            <div class="flex items-center gap-2 sm:gap-3">
              <button
                class="h-10 w-10 rounded-full grid place-items-center text-[#86868b] hover:bg-black/5 dark:hover:bg-white/10 hover:text-black dark:hover:text-white transition-colors"
                @click="toggleDarkMode">
                <span class="material-symbols-outlined text-[22px]">
                  {{ darkMode ? 'light_mode' : 'dark_mode' }}
                </span>
              </button>

              <div class="relative menu-root" @keydown.escape="isProfileMenuOpen = false">
                <button @click="isProfileMenuOpen = !isProfileMenuOpen"
                  class="flex items-center gap-3 pl-1 pr-1 py-1 rounded-full hover:bg-black/5 dark:hover:bg:white/10 transition-colors"
                  :class="{ 'bg-white dark:bg-[#2c2c2e] shadow-sm pr-3': isProfileMenuOpen }">
                  <div
                    class="h-9 w-9 rounded-full bg-gradient-to-b from-[#7B2CBF] to-[#5A189A] grid place-items-center text-white text-sm font-semibold shadow-inner border border-white/20">
                    {{ userInitials }}
                  </div>
                  <span
                    class="hidden sm:block material-symbols-outlined text-[#86868b] text-[20px] transition-transform duration-300"
                    :class="{ 'rotate-180': isProfileMenuOpen }">
                    expand_more
                  </span>
                </button>

                <div v-show="isProfileMenuOpen"
                  class="absolute right-0 mt-3 w-64 rounded-[24px] bg-white/90 dark:bg-[#1c1c1e]/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.15)] dark:shadow-black/50 p-2 z-50 origin-top-right">
                  <div class="space-y-1">
                    <router-link to="/user/editar-perfil" class="profile-item">
                      <span class="material-symbols-outlined">person</span>
                      Meu Perfil
                    </router-link>
                    <button @click="goToSubscription" class="profile-item">
                      <span class="material-symbols-outlined">workspace_premium</span>
                      Gerenciar assinatura
                    </button>
                    <button @click="logout" class="profile-item text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                      <span class="material-symbols-outlined">logout</span>
                      Sair
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </header>

      <main class="flex-1 px-4 sm:px-6 pb-8 pt-2 max-w-[1400px] mx-auto w-full">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </main>
    </div>

    <transition name="slide-over">
      <div v-if="mobileOpen" class="fixed inset-0 z-[60] md:hidden">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="mobileOpen = false"></div>

        <aside class="absolute left-0 top-0 h-full w-[320px]
          pt-[calc(env(safe-area-inset-top)+8px)]
          bg-[#F5F5F7]/95 dark:bg-[#1c1c1e]/95
          shadow-2xl backdrop-blur-2xl
          flex flex-col p-5 overflow-y-auto
          border-r border-black/10 dark:border-white/10">

          <div class="flex justify-between items-center mb-6">
            <span class="text-lg font-semibold text-[#1d1d1f] dark:text-white tracking-tight">
              Menu
            </span>
            <button @click="mobileOpen = false" class="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10">
              <span class="material-symbols-outlined text-[22px] text-[#86868b]">close</span>
            </button>
          </div>

          <nav class="space-y-2">
            <template v-for="(item, index) in menuItems" :key="index">
              <div v-if="item.isHeader"
                class="mt-5 mb-2 px-2 text-[11px] font-bold uppercase tracking-wider text-[#9b9ba1]">
                {{ item.label }}
              </div>

              <div v-else-if="item.children" class="space-y-1">
                <div class="flex items-center gap-3 px-3 py-3 rounded-xl
                     bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white font-medium">
                  <span class="material-symbols-outlined text-[#7B2CBF]">{{ item.icon }}</span>
                  <span>{{ item.label }}</span>
                </div>

                <div class="pl-10 space-y-1">
                  <router-link v-for="child in item.children" :key="child.to" :to="child.to" @click="mobileOpen = false"
                    class="block py-2 text-[14px] text-[#6e6e73] dark:text-[#a1a1a6]
                       hover:text-[#7B2CBF] transition">
                    {{ child.label }}
                  </router-link>
                </div>
              </div>

              <router-link v-else :to="item.to" @click="mobileOpen = false" class="flex items-center gap-3 px-3 py-3 rounded-xl
                   text-[#1d1d1f] dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition">
                <span class="material-symbols-outlined text-[22px]">{{ item.icon }}</span>
                <span class="text-[15px] font-medium">{{ item.label }}</span>
              </router-link>
            </template>
          </nav>

          <div class="mt-8 pt-4 border-t border-black/10 dark:border-white/10">
            <button @click="() => { logout(); mobileOpen = false }" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl
                 text-red-600 dark:text-red-400
                 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
              <span class="material-symbols-outlined">logout</span>
              Sair
            </button>
          </div>
        </aside>
      </div>
    </transition>

  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

const darkMode = ref(localStorage.getItem('darkMode') === 'true')
const isProfileMenuOpen = ref(false)
const collapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')
const mobileOpen = ref(false)
const userName = ref(localStorage.getItem('userName') || 'Usuário')

const userInitials = computed(() => {
  const n = (userName.value || 'U').trim()
  return (
    n
      .split(' ')
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || 'U'
  )
})

const menuItems = ref([
  { isHeader: true, label: 'Principal' },
  { label: 'Início', icon: 'grid_view', to: '/user/dashboard' },
  { label: 'Calendário', icon: 'calendar_month', to: '/user/calendario' },
  { label: 'Tarefas', icon: 'check_circle', to: '/user/tarefas' },
  { label: 'Calculadora', icon: 'calculate', to: '/user/calculadora' },
  { isHeader: true, label: 'Finanças' },
  {
    label: 'Despesas',
    icon: 'arrow_downward',
    isOpen: false,
    children: [
      { label: 'Despesas Gerais', to: '/user/gastos' },
      { label: 'Cartão de Crédito', to: '/user/gastos-cartao' }
    ]
  },
  { label: 'Receitas', icon: 'arrow_upward', to: '/user/receitas' },
  {
    label: 'Assinaturas',
    icon: 'subscriptions',
    isOpen: false,
    children: [
      { label: 'Minhas Assinaturas', to: '/user/assinaturas' },
      { label: 'Compartilhadas', to: '/user/assinaturas-compartilhadas' }
    ]
  },
  {
    label: 'Metas',
    icon: 'flag',
    isOpen: false,
    children: [
      { label: 'Minhas Metas', to: '/user/metas' },
      { label: 'Compartilhadas', to: '/user/metas-compartilhadas' }
    ]
  },
  { label: 'Projetos', icon: 'assignment', to: '/user/orcamento-projeto' },
  { label: 'Financiamentos', icon: 'monetization_on', to: '/user/financiamentos' },
  { isHeader: true, label: 'Patrimônio' },
  {
    label: 'Contas & Cofre',
    icon: 'account_balance',
    isOpen: false,
    children: [
      { label: 'Saldo em Contas', to: '/user/saldo-banco' },
      { label: 'Dinheiro no Cofre', to: '/user/dinheiro-cofre' }
    ]
  },
  {
    label: 'Investimentos',
    icon: 'monitoring',
    isOpen: false,
    children: [
      { label: 'Meus Investimentos', to: '/user/investimentos' },
      { label: 'Compartilhados', to: '/user/investimentos-compartilhados' }
    ]
  },
  { label: 'Conversor', icon: 'currency_exchange', to: '/user/conversor' },
  { isHeader: true, label: 'Outros' },
  { label: 'Relatórios', icon: 'pie_chart', to: '/user/relatorios' }
])

const toggleSidebar = () => {
  collapsed.value = !collapsed.value
  localStorage.setItem('sidebarCollapsed', collapsed.value.toString())
}

const toggleSubmenu = (item) => {
  if (collapsed.value) {
    collapsed.value = false
    setTimeout(() => {
      item.isOpen = true
    }, 150)
  } else {
    item.isOpen = !item.isOpen
  }
}

const isActive = (path) => route.path === path

const isParentActive = (item) => {
  if (item.children) return item.children.some((child) => child.to === route.path)
  return false
}

const checkActiveGroup = () => {
  menuItems.value.forEach((item) => {
    if (item.children && isParentActive(item)) item.isOpen = true
  })
}

const applyTheme = () => {
  document.documentElement.classList.toggle('dark', darkMode.value)
}

const toggleDarkMode = () => {
  darkMode.value = !darkMode.value
  localStorage.setItem('darkMode', darkMode.value.toString())
  applyTheme()
}

const goToSubscription = () => {
  router.push('/user/minha-assinatura')
}

const logout = () => {
  localStorage.clear()
  router.push('/')
}

const onDocClick = (e) => {
  if (!e.target?.closest?.('.menu-root')) isProfileMenuOpen.value = false
}

onMounted(() => {
  applyTheme()
  checkActiveGroup()
  document.addEventListener('click', onDocClick)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
})

watch(
  () => route.path,
  () => {
    checkActiveGroup()
  }
)
</script>

<style scoped>
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.custom-scroll::-webkit-scrollbar {
  width: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.dark .custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.nav-item {
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.nav-item:hover:not(.active) {
  background-color: rgba(0, 0, 0, 0.04);
}

.dark .nav-item:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.08);
}

.nav-item.active {
  background: linear-gradient(135deg, #7b2cbf, #5a189a);
  box-shadow: 0 4px 12px rgba(123, 44, 191, 0.3);
}

.profile-item {
  @apply flex items-center gap-3 px-3 py-2.5 rounded-[12px] text-[14px] font-medium text-[#1d1d1f] dark:text-[#f5f5f7] hover:bg-black/5 dark:hover:bg-white/10 transition-colors cursor-pointer w-full text-left;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-over-enter-active,
.slide-over-leave-active {
  transition: all 0.3s ease;
}

.slide-over-enter-from,
.slide-over-leave-to {
  opacity: 0;
}

.slide-over-enter-from aside,
.slide-over-leave-to aside {
  transform: translateX(-100%);
}
</style>
